import React, { ChangeEvent, FocusEvent, FormEvent, useState } from 'react';
import { styled } from '@mui/material';
import { Button, Link, Paper, TextField, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import EmailValidator from 'email-validator';
import { isNull, isUndefined } from 'lodash';
import validate from 'validate.js';
import { ERROR_401 } from 'common/config/api';
import * as messages from 'common/constants/messages/validations';
import { Alert } from 'components';
import { Severity } from 'components/Alert/type';
import { useSendPasswordReset } from 'services/hooks/auth';
import { differentThanValidator, excludeWhiteSpacesValidator } from 'utilities';
import { CONSTRAINTS_PASSWORD_RESET, DEFAULT_ERROR_MESSAGE } from './constants';
import { FormStateProps, PasswordResetProps } from './ResetPasswordForm.props';
import { StyledButton, StyledLink, StyledPaper, StyledSubtitle, StyledTextField } from './ResetPasswordForm.styles';

validate.validators.excludeWhiteSpaces = excludeWhiteSpacesValidator;
validate.validators.differentThan = differentThanValidator;

const ResetPasswordForm = ({ setPasswordRecovery }: PasswordResetProps) => {
  const sendPasswordReset = useSendPasswordReset();
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [formState, setFormState] = useState<FormStateProps>({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  const [severity, setSeverity] = useState<Severity>('error');

  const hasError = (field: string) =>
    !!formState.touched[field] && !!formState.errors[field] && !!formState.errors[field].length;

  const handleResetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsAlertVisible(false);

    if (formState.isValid && formState.values.mail) {
      setIsLoading(true);

      const response = await sendPasswordReset(formState.values.mail);

      setIsLoading(false);
      if (isNull(response)) {
        // status code 204 => null body
        setSeverity('success');
        setErrorMessage(messages.PASSWORD_CHANGE_SEND_MAIL);
        setIsAlertVisible(true);
      } else {
        setIsAlertVisible(true);
        setSeverity('error');
        if (response?.statusCode === ERROR_401) {
          setErrorMessage(response.body.detail);
        }
        setErrorMessage(messages.PASSWORD_RESET_FAILED);
      }
    }
  };

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    handleResetPassword(event as unknown as FormEvent<HTMLFormElement>);
  };

  const handleBackClick = () => {
    setPasswordRecovery(false);
  };

  const emailIsValid = (event: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = event.target.value;
    const tmpFormState = { ...formState };
    tmpFormState.values.mail = fieldValue;
    setFormState(tmpFormState);
    const { mail } = formState.values;
    return EmailValidator.validate(mail ?? '');
  };

  const [sendEmailButtonIsDisabled, setSendEmailButtonIsDisabled] = useState(true);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSendEmailButtonIsDisabled(!emailIsValid(event));
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const tmpFormState = { ...formState };
    const fieldName = event.target.name;
    const fieldValue = event.target.value;
    const errors = validate({ ...formState.values, [fieldName]: fieldValue }, CONSTRAINTS_PASSWORD_RESET);

    tmpFormState.values[fieldName] = fieldValue;
    tmpFormState.touched[fieldName] = true;
    tmpFormState.isValid = isUndefined(errors);
    tmpFormState.errors = errors ?? {};

    setFormState(tmpFormState);

    if (errors && errors.mail.length > 0) {
      setIsAlertVisible(true);
    } else {
      setIsAlertVisible(false);
    }
  };

  return (
    <>
      <StyledPaper>
        <Typography variant="h2">Reset password</Typography>
        <StyledSubtitle>Enter the email linked to the account you want to reset the password for</StyledSubtitle>
        <Alert isAlertVisible={isAlertVisible} severity={severity}>
          <>{errorMessage}</>
        </Alert>
        <form id="password-reset-form" onSubmit={handleResetPassword}>
          <StyledTextField
            id="login-username-input"
            name="mail"
            label="Email"
            fullWidth
            type="text"
            className="scar-textField"
            error={isAlertVisible && hasError('mail')}
            helperText={isAlertVisible && hasError('mail') ? formState.errors.mail[0] : null}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
          />
          <StyledButton
            id="submit-action-btn"
            variant="contained"
            color="secondary"
            fullWidth
            type="submit"
            onClick={handleSubmit}
            disabled={sendEmailButtonIsDisabled}
            data-testid="send_mail_button">
            {isLoading ? <CircularProgress size={28} color="inherit" /> : 'Email me a recovery link'}
          </StyledButton>
        </form>
      </StyledPaper>
      <StyledLink id="login-link" onClick={handleBackClick} variant="h5">
        LOG IN
      </StyledLink>
    </>
  );
};

export default ResetPasswordForm;
